import type { EnvironmentId as EnvironmentIdType } from "@t3tools/contracts";
import { WS_METHODS } from "@t3tools/contracts";
import * as Stream from "effect/Stream";
import { Atom } from "effect/unstable/reactivity";

import type { EnvironmentRegistry } from "../connection/registry.ts";
import { subscribe } from "../rpc/client.ts";
import { followStreamInEnvironment } from "./runtime.ts";

/**
 * A single "something changed; refetch" tick for the current user, carrying a
 * monotonically increasing `seq` so a consumer can detect a NEW poke even when
 * two consecutive pokes have the same kind/threadId. See server `MorrowSignalHub`.
 */
export interface MorrowSignalTick {
  readonly seq: number;
  readonly kind: "allowances" | "comms" | "agents";
  readonly threadId: string | null;
}

export const INITIAL_MORROW_SIGNAL_TICK: MorrowSignalTick = Object.freeze({
  seq: 0,
  kind: "allowances",
  threadId: null,
});

/**
 * Per-environment atom that follows the `subscribeMorrowSignals` WS stream and
 * exposes the latest tick. The plain-React allowance/comms components read this
 * (via the apps/web `useMorrowSignal` hook) and refetch on a new matching tick
 * instead of polling on a fast timer.
 */
export function createMorrowSignalAtoms<R, E>(
  runtime: Atom.AtomRuntime<EnvironmentRegistry | R, E>,
) {
  const tickAtom = Atom.family((environmentId: EnvironmentIdType) =>
    runtime.atom(
      followStreamInEnvironment(
        environmentId,
        subscribe(WS_METHODS.subscribeMorrowSignals, {}).pipe(
          Stream.mapAccum(
            () => 0,
            (seq, event) => {
              const next = seq + 1;
              return [next, [{ seq: next, kind: event.kind, threadId: event.threadId }]] as const;
            },
          ),
        ),
      ),
      { initialValue: INITIAL_MORROW_SIGNAL_TICK },
    ),
  );

  return { tickAtom };
}
